import { useState } from 'react';

/**
 * The Security & passkeys modal.
 *
 * Several steps target things that only render in some states — the passkey
 * list is absent until you have one, "sign out everywhere else" until you are
 * signed in somewhere else. They are left in and skipped by the
 * target_not_found branch rather than gated, because which of them apply is
 * exactly what a first-time reader is trying to work out.
 */
function useSecurityTour() {
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    const steps = [
        {
            target: '#passkeys-section',
            title: 'Passkeys',
            content: 'A passkey lets you sign in with your fingerprint, face or screen lock instead of typing a password. Your password keeps working either way — this is an addition, not a replacement.',
            disableBeacon: true,
        },
        {
            target: '#add-passkey-button',
            title: 'Set up a device',
            content: 'Adds a passkey for whatever you are using right now. You can add several — a laptop and a phone — and any of them will get you in.',
            disableBeacon: true,
        },
        {
            target: '#passkeys-list',
            title: 'What you have added',
            content: 'Each passkey is named after the device it was made on, and says whether it is synced to your other devices or lives only on that one. That distinction is what decides whether losing a phone is an inconvenience or a lockout.',
            disableBeacon: true,
        },
        {
            target: '#sessions-section',
            title: 'Signed-in devices',
            content: 'Everywhere your account is currently signed in, with when and how each one signed in.',
            disableBeacon: true,
        },
        {
            target: '#revoke-others-button',
            title: 'If something goes missing',
            content: 'Signs out every device except this one. This is the button for a lost phone — and change your password afterwards.',
            disableBeacon: true,
        },
    ];

    const handleStartTutorial = () => {
        setRunTour(false);
        setStepIndex(0);
        setTimeout(() => {
            setStepIndex(0);
            setRunTour(true);
        }, 100);
    };

    const handleJoyrideCallback = (data) => {
        const { status, action, type, index, origin } = data;

        if (origin === 'overlay' && action === 'close') return;

        if (type === 'error:target_not_found' || status === 'error') {
            if (index + 1 < steps.length) {
                setStepIndex(index + 1);
                return;
            }
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (status === 'finished' || status === 'skipped' || action === 'close' || action === 'stop') {
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (type === 'step:after' && action === 'next') {
            setStepIndex(index + 1);
        } else if (type === 'step:after' && action === 'prev') {
            if (index - 1 < 0) {
                setRunTour(false);
                setStepIndex(0);
                return;
            }
            setStepIndex(index - 1);
        }
    };

    return { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback };
}

export default useSecurityTour;
